import { useCallback, useMemo, useState } from 'react'
import type { Aggregates, AreaId, PulseResponse } from '../types/pulse'
import { deriveAggregates } from '../utils/pulse'

const emptyResponse: PulseResponse = {
  ranking: [],
  topArea: null,
  followUpChoice: null,
  followUpFreeText: '',
  teamRating: null,
  techRating: null,
  openResponse: '',
  submittedAt: null,
}

export interface PulseStore {
  response: PulseResponse
  aggregates: Aggregates
  submitRanking: (order: AreaId[]) => void
  submitFollowUp: (choice: string, freeText: string) => void
  submitRating: (key: 'team' | 'tech', value: number) => void
  submitOpen: (text: string) => void
  reset: () => void
}

export function usePulse(): PulseStore {
  const [response, setResponse] = useState<PulseResponse>(emptyResponse)
  const aggregates = useMemo(() => deriveAggregates(response), [response])

  const stamp = (prev: PulseResponse) => prev.submittedAt ?? new Date().toISOString()

  const submitRanking = useCallback((order: AreaId[]) => {
    setResponse((prev) => {
      const top = order[0] ?? null
      const areaChanged = top !== prev.topArea
      return {
        ...prev,
        ranking: order,
        topArea: top,
        followUpChoice: areaChanged ? null : prev.followUpChoice,
        followUpFreeText: areaChanged ? '' : prev.followUpFreeText,
        submittedAt: stamp(prev),
      }
    })
  }, [])

  const submitFollowUp = useCallback((choice: string, freeText: string) => {
    setResponse((prev) => ({ ...prev, followUpChoice: choice, followUpFreeText: freeText }))
  }, [])

  const submitRating = useCallback((key: 'team' | 'tech', value: number) => {
    setResponse((prev) => ({
      ...prev,
      [key === 'team' ? 'teamRating' : 'techRating']: value,
    }))
  }, [])

  const submitOpen = useCallback((text: string) => {
    setResponse((prev) => ({ ...prev, openResponse: text }))
  }, [])

  const reset = useCallback(() => setResponse(emptyResponse), [])

  return { response, aggregates, submitRanking, submitFollowUp, submitRating, submitOpen, reset }
}
